/* ==========================================================================
   Satjeevan — Hero Slider Section JS (hero-slider.js)

   Progressively enhances the server-rendered slide deck (sections/
   hero-slider.liquid) into a crossfade carousel:
     - Auto-play with pause-on-hover / pause-on-focus, resumes after
     - Previous / next arrow buttons
     - Pagination dots
     - Touch swipe
     - Left / Right arrow-key navigation
     - Screen-reader live-region slide announcements
     - Only the active slide's controls are keyboard-tabbable
     - Shopify Theme Editor: selecting a slide block jumps the deck to it

   No dependencies — vanilla JS only.
   ========================================================================== */

(function () {
  'use strict';

  var AUTOPLAY_MS = 6000;
  var SWIPE_THRESHOLD = 40;
  var INIT_ATTR = 'data-hero-slider-init';

  /**
   * @param {HTMLElement} root
   */
  function initHeroSlider(root) {
    if (root.hasAttribute(INIT_ATTR)) return;
    root.setAttribute(INIT_ATTR, '1');

    /** @type {HTMLElement[]} */
    var slides = Array.prototype.slice.call(root.querySelectorAll('.hero-slide'));
    if (slides.length < 2) return; // single static slide — nothing to animate

    var dotsWrap = root.querySelector('[data-hero-dots]');
    /** @type {HTMLElement[]} */
    var dots = dotsWrap ? Array.prototype.slice.call(dotsWrap.querySelectorAll('.hero-dot')) : [];
    var prevBtn = /** @type {HTMLElement | null} */ (root.querySelector('[data-hero-prev]'));
    var nextBtn = /** @type {HTMLElement | null} */ (root.querySelector('[data-hero-next]'));
    var statusEl = root.querySelector('[data-hero-status]');

    var reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    var current = slides.indexOf(/** @type {HTMLElement} */ (root.querySelector('.hero-slide.is-active')));
    if (current < 0) current = 0;

    /** @type {number | null} */
    var timer = null;
    var hoverPaused = false;
    var focusPaused = false;

    /**
     * Only the active slide's links/buttons should be reachable by keyboard
     * or exposed to assistive tech — inactive slides sit invisibly on top of
     * each other and must never trap focus or be announced.
     * @param {HTMLElement} slide
     * @param {boolean} isActive
     */
    function setSlideInteractivity(slide, isActive) {
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

      var focusables = slide.querySelectorAll('a[href], button');
      for (var i = 0; i < focusables.length; i++) {
        if (isActive) {
          focusables[i].removeAttribute('tabindex');
        } else {
          focusables[i].setAttribute('tabindex', '-1');
        }
      }
    }

    /**
     * @param {number} index
     */
    function announce(index) {
      if (!statusEl) return;
      var heading = slides[index].querySelector('.hero-slide-head');
      var text = 'Slide ' + (index + 1) + ' of ' + slides.length;
      if (heading && heading.textContent) {
        text += ': ' + heading.textContent.trim();
      }
      statusEl.textContent = text;
    }

    /**
     * @param {number} index
     * @param {{ silent?: boolean }} [opts]
     */
    function goTo(index, opts) {
      var silent = !!(opts && opts.silent);
      var nextIndex = ((index % slides.length) + slides.length) % slides.length;
      if (nextIndex === current && !silent) return;

      setSlideInteractivity(slides[current], false);
      if (dots[current]) {
        dots[current].classList.remove('is-active');
        dots[current].removeAttribute('aria-selected');
      }

      current = nextIndex;

      setSlideInteractivity(slides[current], true);
      if (dots[current]) {
        dots[current].classList.add('is-active');
        dots[current].setAttribute('aria-selected', 'true');
      }

      if (!silent) announce(current);
      restart();
    }

    function goNext() {
      goTo(current + 1);
    }

    function goPrev() {
      goTo(current - 1);
    }

    function stop() {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    }

    function restart() {
      stop();
      if (reducedMotionQuery.matches || hoverPaused || focusPaused) return;
      timer = setInterval(goNext, AUTOPLAY_MS);
    }

    // Server-rendered markup only marks slide 1 as interactive; sync the rest
    // in case a Theme Editor re-render left a different slide "active".
    slides.forEach(function (slide, i) {
      setSlideInteractivity(slide, i === current);
    });

    if (nextBtn) nextBtn.addEventListener('click', goNext);
    if (prevBtn) prevBtn.addEventListener('click', goPrev);

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        goTo(i);
      });
    });

    // Pause on hover, resume once the pointer leaves the slider.
    root.addEventListener('mouseenter', function () {
      hoverPaused = true;
      stop();
    });
    root.addEventListener('mouseleave', function () {
      hoverPaused = false;
      restart();
    });

    // Pause while any control inside the slider has keyboard focus, resume
    // once focus has moved completely outside it.
    root.addEventListener('focusin', function () {
      focusPaused = true;
      stop();
    });
    root.addEventListener('focusout', function (event) {
      var toElement = /** @type {FocusEvent} */ (event).relatedTarget;
      if (toElement instanceof Node && root.contains(toElement)) return;
      focusPaused = false;
      restart();
    });

    // Left / Right arrow-key navigation while focus is within the slider.
    root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goNext();
      }
    });

    // Touch swipe.
    /** @type {number | null} */
    var touchStartX = null;
    root.addEventListener(
      'touchstart',
      function (event) {
        touchStartX = event.touches[0].clientX;
      },
      { passive: true }
    );
    root.addEventListener('touchend', function (event) {
      if (touchStartX === null) return;
      var deltaX = event.changedTouches[0].clientX - touchStartX;
      if (deltaX > SWIPE_THRESHOLD) {
        goPrev();
      } else if (deltaX < -SWIPE_THRESHOLD) {
        goNext();
      }
      touchStartX = null;
    });

    // Shopify Theme Editor: jump to whichever slide's block is selected, and
    // keep autoplay paused while merchants are actively editing a slide.
    root.addEventListener('shopify:block:select', function (event) {
      var target = /** @type {HTMLElement} */ (event.target);
      var slide = target && target.closest ? target.closest('.hero-slide') : null;
      if (!slide) return;
      var index = slides.indexOf(/** @type {HTMLElement} */ (slide));
      if (index > -1) goTo(index, { silent: true });
      stop();
    });
    root.addEventListener('shopify:block:deselect', function () {
      restart();
    });

    restart();
  }

  function init() {
    var sliders = document.querySelectorAll('.hero-slider');
    sliders.forEach(function (slider) {
      initHeroSlider(/** @type {HTMLElement} */ (slider));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify Theme Editor live-reload support.
  document.addEventListener('shopify:section:load', function (event) {
    var target = /** @type {HTMLElement} */ (event.target);
    if (target && target.querySelector && target.querySelector('.hero-slider')) {
      init();
    }
  });
})();
